'use client';

import {Tab, TabGroup, TabList, TabPanel, TabPanels} from "@headlessui/react";
import Image from '@/components/ui/MediaImage';
import { useEffect, useState } from "react";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { transformMediaUrl } from "@/lib/media-url-transformer";

interface ReviewData {
  id: number;
  name: string;
  title: string | null;
  comment: string;
  rating: number | null;
  image: string | null;
  submitted_at: string | null;
}

interface AboutCustomerReviewsTabProps {
  /**
   * Reviews verisi (server-side'dan props ile gelir)
   * Eğer sağlanmazsa ve enableClientFetch true ise client-side'da çekilir
   */
  reviews?: ReviewData[];
  /**
   * Review ID'leri (client-side fetch için)
   * Eğer reviews props olarak sağlanmışsa, bu prop kullanılmaz
   */
  reviewIds?: number[];
  reviewableType?: string;
  reviewableId?: number;
  siteId?: number;
  languageId?: number;
  language?: string;
  languageCode?: string;
  /**
   * Sol resim (media ID, URL veya obje formatında)
   */
  leftImage?: number | string | { mediaId?: number; url?: string; alt?: string } | null;
  /**
   * Sağ resim (media ID, URL veya obje formatında)
   */
  rightImage?: number | string | { mediaId?: number; url?: string; alt?: string } | null;
  /**
   * Props'tan veri gelmezse client-side'da veri çekmeyi etkinleştir
   * GrapeJS preview için kullanışlıdır
   */
  enableClientFetch?: boolean;
  __pageContext?: {
    siteId?: number;
    languageId?: number;
  };
}

// Fallback görseller (yorum görseli yoksa kullanılacak)
const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=450&h=380&fit=crop",
  "https://images.unsplash.com/photo-1500375592092-40eb2168fd21?w=450&h=380&fit=crop"
];

const ABOUT_REVIEWS_SECTION =
  'mx-auto w-full min-w-0 max-w-9/12 px-4 py-12 sm:px-6 sm:py-20 md:py-28 lg:py-[190px]';

export default function AboutCustomerReviewsTab(props: AboutCustomerReviewsTabProps = {}) {
  const {
    reviews: initialReviews,
    reviewIds,
    reviewableType,
    reviewableId,
    siteId: propSiteId,
    languageId: propLanguageId,
    language,
    languageCode,
    leftImage,
    rightImage,
    enableClientFetch = false,
    __pageContext
  } = props;

  const [reviews, setReviews] = useState<ReviewData[]>(initialReviews || []);
  const [loading, setLoading] = useState(!initialReviews);
  const [error, setError] = useState<string | null>(null);
  
  // Language variables için state
  const [variables, setVariables] = useState<Record<string, string>>({});
  
  // Helper function to get variable with fallback
  const getVariable = (key: string, fallback: string): string => {
    return variables[key] || fallback;
  };

  // Language ID'yi belirle (öncelik sırası: prop > pageContext)
  // site_id artık gerekmiyor - Laravel API key'den otomatik bulunuyor
  const languageId = propLanguageId || __pageContext?.languageId;

  // Props'tan veri geldiyse client-side fetch yapma
  const hasInitialData = initialReviews && initialReviews.length > 0;

  // Language variables - Context'ten al (duplicate fetch'i önlemek için)
  const { variables: contextVariables } = useLanguageVariables();
  
  // Context'ten gelen variables'ı kullan, yoksa boş object
  useEffect(() => {
    if (contextVariables && Object.keys(contextVariables).length > 0) {
      setVariables(contextVariables);
    }
  }, [contextVariables]);

  useEffect(() => {
    // Props'tan veri geldiyse client-side fetch yapma
    if (hasInitialData || !enableClientFetch) {
      setLoading(false);
      return;
    }

    async function fetchReviews() {
      // Eğer ne reviewIds ne de reviewableType/reviewableId varsa, API'ye istek atma
      if ((!reviewIds || reviewIds.length === 0) && (!reviewableType || !reviewableId)) {
        setLoading(false);
        setReviews([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // API parametrelerini oluştur
        // site_id göndermiyoruz - Laravel API key'den otomatik bulacak
        const params = new URLSearchParams();
        
        if (languageId) {
          params.set('language_id', String(languageId));
        }

        // reviewIds varsa öncelikli olarak kullan
        if (reviewIds && reviewIds.length > 0) {
          reviewIds.forEach(id => params.append('review_ids[]', String(id)));
        } else if (reviewableType && reviewableId) {
          // reviewableType/reviewableId ile filtreleme (Product için gelecekte)
          params.set('reviewable_type', reviewableType);
          params.set('reviewable_id', String(reviewableId));
        }

        const response = await fetch(`/api/customer-reviews?${params.toString()}`);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setReviews(data.data || []);
      } catch (err) {
        console.error('Yorumlar yüklenirken hata:', err);
        const errorMessage = err instanceof Error ? err.message : getVariable('reviews_load_error', 'Reviews could not be loaded');
        setError(errorMessage);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    }

    fetchReviews();
  }, [languageId, reviewIds, reviewableType, reviewableId, hasInitialData, enableClientFetch]);

  // Loading state
  if (loading) {
    return (
      <section className={ABOUT_REVIEWS_SECTION}>
        <div className="text-center text-gray-500">{getVariable('reviews_loading', 'Loading reviews...')}</div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className={ABOUT_REVIEWS_SECTION}>
        <div className="text-center text-red-500">{getVariable('reviews_error_prefix', 'Error: ')}{error}</div>
      </section>
    );
  }

  // Empty state - props yoksa veya yorum yoksa
  const hasData = reviews.length > 0;
  const missingSelection = !enableClientFetch && !hasData && (!reviewIds || reviewIds.length === 0) && (!reviewableType || !reviewableId);
  const hasFetchedButEmpty = enableClientFetch && !loading && !hasData && ((reviewIds && reviewIds.length > 0) || (reviewableType && reviewableId));

  if (missingSelection) {
      return (
        <section className={ABOUT_REVIEWS_SECTION}>
        <div className="text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl p-8">
          {getVariable('reviews_no_selection', 'No review selection made for this component. Please select the component in the page editor and use the "Select Reviews" button.')}
          </div>
        </section>
      );
    }
    
  // API'den veri çekildi ama boş geldi (DB'de tanımlanmamış)
  if (hasFetchedButEmpty) {
    return (
      <section className={ABOUT_REVIEWS_SECTION}>
        <div className="text-center text-alsero-light-gray">
          {getVariable('reviews_no_reviews_lang', 'No reviews found for this language yet.')}
        </div>
      </section>
    );
  }
  
  // Props var ama yorum yok veya loading sonrası boş
  if (!hasData) {
    return (
      <section className={ABOUT_REVIEWS_SECTION}>
        <div className="text-center text-alsero-light-gray">
          {getVariable('reviews_no_reviews', 'No reviews available yet.')}
        </div>
      </section>
    );
  }

  // Resim URL'lerini hazırla ve transform et
  // MEDIA_PROXY_GUIDE.md'ye göre: Component'lerde transform yapma, sadece kullan
  // Server-side'da zaten transform edilmiş geliyor
  const getImageUrl = (imageProp: number | string | { mediaId?: number; url?: string } | null | undefined): string | null => {
    if (!imageProp) return null;
    
    // Eğer obje formatındaysa (mediaId veya url içeriyor)
    // Server-side'da zaten transform edilmiş URL geliyor
    if (typeof imageProp === 'object' && 'url' in imageProp) {
      return imageProp.url || null;
    }
    // Eğer string ise direkt URL (zaten transform edilmiş)
    else if (typeof imageProp === 'string') {
      return imageProp;
    }
    // Eğer number ise mediaId, server-side'da resolve edilmiş olmalı
    // Eğer hala number ise, bu bir hata (server-side resolve çalışmamış)
    else if (typeof imageProp === 'number') {
      return null;
    }
    
    return null;
  };

  // leftImage ve rightImage URL'lerini al ve transform et
  const leftImageUrl = getImageUrl(leftImage);
  const rightImageUrl = getImageUrl(rightImage);
  
  // Resimler için fallback - eğer leftImage veya rightImage yoksa FALLBACK_IMAGES kullan
  const reviewImages = [
    leftImageUrl || FALLBACK_IMAGES[0],
    rightImageUrl || FALLBACK_IMAGES[1]
  ];

  // Yorumları component formatına dönüştür
  const formattedReviews = reviews.map((review) => ({
          user: {
      name: review.name,
      title: review.title || '',
      img: review.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.name)}&size=150&background=random`
          },
          detail: {
      message: review.comment,
      imgs: reviewImages // leftImage ve rightImage'dan gelen resimler veya fallback
          }
  }));
      
    return (
        <div className={ABOUT_REVIEWS_SECTION}> 
            <TabGroup>
                <TabPanels>
                    {formattedReviews.map((review, index) => (
                      <TabPanel key={index}>
                          <div className="grid grid-cols-12 items-center">
                              <div className="col-span-4 xl:h-[600px]">
                                  {review.detail.imgs.map((img, subImgIndex) => (
                                      <div className={[
                                        "px-2 pt-2 pb-16 relative w-[330px]",
                                        subImgIndex % 2 === 1 ? "bg-alsero-soft-white rotate-[21deg] z-0 mt-12 translate-x-4/12 -translate-y-full" : "bg-white rotate-[-14deg] z-1"
                                      ].join(" ")}
                                      key={subImgIndex}>
                                          <Image 
                                            src={img} 
                                            alt={review.user.name} 
                                            width={330} 
                                            height={400} 
                                            className="w-[330px] h-[400px] object-cover" 
                                            quality={90}
                                            unoptimized={img.startsWith('/api/media/proxy/')}
                                          />
                                      </div>
                                  ))}
                              </div>
                              <div className="col-start-6 col-span-5 flex flex-col">
                                  <div className="flex flex-col gap-1">
                                      <h4 className="text-[#081E2A] text-2xl font-bold">{review.user.name}</h4>
                                      <p className="text-alsero-light-gray text-lg leading-8 -tracking-[%2]">{review.user.title}</p>
                                  </div>
                                  <p className="mt-6 text-[#081E2A] text-2xl leading-10 -tracking-[2%] font-bold">{review.detail.message}</p>
                              </div>
                          </div>
                      </TabPanel>
                    ))}
                </TabPanels>
                <div className="grid grid-cols-12 -translate-y-full">
                    <div className="col-start-6 col-span-4
                    before:content-[''] before:bg-alsero-first before:w-[50px] before:h-[4px] rounded-[2px] before:block before:mb-6">
                        <div className="flex flex-row justify-between items-center">
                            <TabList className="flex flex-row gap-3">
                                {formattedReviews.map((review, index) => (
                                    <Tab className="w-[72px] h-[72px] border-[3px] border-white cursor-pointer rounded-full data-[selected]:border-alsero-first data-[hover]:border-alsero-first/50 focus:!outline-none focus:!ring-0 focus:!border-alsero-first transition-colors ease-in-out duration-300" key={index}>
                                        <Image 
                                          src={review.user.img} 
                                          alt={review.user.name} 
                                          width={72} 
                                          height={72} 
                                          className="w-full h-full rounded-full object-cover" 
                                          quality={90}
                                          unoptimized={review.user.img.startsWith('/api/media/proxy/')}
                                        />
                                    </Tab>
                                ))}
                            </TabList>
                            <div className="border border-[#7A8697]/15 p-8">
                                <svg width="38" height="28" viewBox="0 0 38 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M37.4637 13.4973L37.2789 -0.0014559L22.0532 0.200781L22.238 13.6996L32.3891 13.5647C32.4188 15.9507 31.3798 18.2524 29.5002 19.9648C27.6206 21.6771 25.0538 22.6604 22.363 22.6987L22.4246 27.1983C26.4608 27.1408 30.311 25.6659 33.1304 23.0974C35.9499 20.5288 37.5082 17.0763 37.4637 13.4973V13.4973Z" fill="#E31E24"/>
                                    <path d="M1.04571 22.9839L1.1073 27.4835C5.14384 27.4264 8.99453 25.9517 11.8144 23.3831C14.6342 20.8145 16.1928 17.3617 16.1482 13.7825L15.9635 0.2837L0.737766 0.485937L0.922535 13.9847L11.0718 13.8499C11.1015 16.2359 10.0626 18.5376 8.18294 20.2499C6.30332 21.9623 3.73652 22.9455 1.04571 22.9839Z" fill="#E31E24"/>
                                </svg>
                            </div>
                        </div>

                    </div>
                </div>
            </TabGroup>
        </div>
    )
}